import { Flex } from '@chakra-ui/react-v2';
import { any } from 'ramda';
import { useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';

import {
  Menu,
  MenuList,
  MenuGroup,
  MenuItem,
  HStack,
  MenuDivider,
  MenuButton,
  Box,
  Tooltip,
  VisuallyHidden,
  useDisclosure,
  Avatar,
  SwapIcon,
} from '@frontend/legacy-components';
import { mouseKeyBindingService } from '@frontend/mousekeybindings';

import { PERMISSIONS } from 'constants/all';

import { logout } from 'modules/auth/login/data/actions';
import { copyAnalyticsDrawer } from 'modules/common/drawers/copyAnalyticsDrawer/data/actions';
import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';
import { featureFlagDrawer } from 'modules/common/drawers/featureFlagDrawer/data/actions';
import { managedIntegrationDrawer } from 'modules/common/drawers/managedIntegrationCreateEditDrawer/data/actions';
import { superUserChangeUserGroupModal } from 'modules/common/userGroupManagement/data/actions';
import SwitchUserGroupDrawer from 'modules/common/userGroupManagement/switchUserGroup/SwitchUserGroupDrawer';
import useGetActiveUserGroupMembers from 'modules/common/userGroupManagement/switchUserGroup/hooks/useGetActiveUserGroupMembers';
import { useDashboardViewContext } from 'modules/dashboard/DashboardViewContext';
import { useIsBelowMaxManagedIntegrations } from 'modules/settings/integrations/hooks/useIsBelowMaxManagedIntegrations';

import { useFeatureFlagValue } from 'store/session/hooks';
import {
  getGroupMemberSupplierRoles,
  getIsLoggedIn,
  getUser,
  getGroupMemberId,
  getUserFullName,
} from 'store/session/selectors';

import Nameplate from './Nameplate';
import { partLibraryConfig, quotingToolConfig } from './navDropDownConfigs';

const MenuContents = ({
  isQuotingToolAvailable,
  isSuperuser,
  logout,
  openChangeUserGroupModal,
  openFeatureFlagDrawer,
  openCopyAnalyticsDrawer,
  supplierRoles,
  openManagedIntegrationDrawer,
  onOpen,
  isUserMultipleGroupMember,
}) => {
  const config = isQuotingToolAvailable ? quotingToolConfig : partLibraryConfig;
  const isBelowMaximum = useIsBelowMaxManagedIntegrations();
  const disabledButtonLabel =
    'You have reached your account’s maximum number of custom integrations.\nContact our support team to request more.';
  const {
    canToggleDashboardView,
    isMultisiteDashboardToggled,
    toggleDashboardView,
  } = useDashboardViewContext();
  const { pathname } = useLocation();
  const isOnDashboard = pathname === '/home';

  return (
    <>
      {any(
        (link) =>
          supplierRoles[link.supplierRoleName] !== PERMISSIONS.NO_ACCESS,
        config,
      ) && (
        <>
          <MenuGroup title="Account">
            {config.map((link) =>
              supplierRoles[link.supplierRoleName] ===
              PERMISSIONS.NO_ACCESS ? null : link.external ? (
                <MenuItem
                  icon={link.icon}
                  key={link.key}
                  as="a"
                  href={link.url}
                  _hover={{ color: 'unset' }}
                >
                  {link.title}
                </MenuItem>
              ) : (
                <MenuItem
                  icon={link.icon}
                  key={link.key}
                  as={Link}
                  to={link.url}
                  _hover={{ color: 'unset' }}
                >
                  {link.title}
                </MenuItem>
              ),
            )}
          </MenuGroup>
          <MenuDivider />
          {isUserMultipleGroupMember && !isSuperuser && (
            <>
              <MenuItem
                icon={<SwapIcon />}
                key={'switchOrganization'}
                _hover={{ color: 'unset' }}
                onClick={onOpen}
              >
                Switch organization
              </MenuItem>
              <MenuDivider />
            </>
          )}
        </>
      )}
      {isSuperuser && (
        <>
          <MenuGroup title="Superuser">
            <MenuItem
              color="interaction.superuser"
              onClick={openChangeUserGroupModal}
            >
              Change Organization
            </MenuItem>
            <MenuItem
              color="interaction.superuser"
              onClick={openFeatureFlagDrawer}
            >
              Edit Feature Flags
            </MenuItem>
            <MenuItem
              color="interaction.superuser"
              onClick={openCopyAnalyticsDrawer}
            >
              Copy Analytics
            </MenuItem>
            <Tooltip
              whiteSpace="pre-wrap"
              label={!isBelowMaximum ? disabledButtonLabel : ''}
            >
              <MenuItem
                color="interaction.superuser"
                onClick={openManagedIntegrationDrawer}
                isDisabled={!isBelowMaximum}
              >
                Superuser Create Integration
              </MenuItem>
            </Tooltip>
            {isOnDashboard && canToggleDashboardView && (
              <MenuItem
                color="interaction.superuser"
                onClick={() => toggleDashboardView()}
              >
                Switch to{' '}
                {isMultisiteDashboardToggled
                  ? 'Single Site Dashboard'
                  : 'Multi-Site Dashboard'}
              </MenuItem>
            )}
          </MenuGroup>
          <MenuDivider />
        </>
      )}
      <MenuGroup>
        <MenuItem color="interaction.destructive" onClick={logout} mt={1}>
          Log Out
        </MenuItem>
      </MenuGroup>
    </>
  );
};

const NavDropdown = ({ isMicroAppHeader }) => {
  const dispatch = useDispatch();

  const user = useSelector(getUser);
  const userFullName = useSelector(getUserFullName);
  const groupMemberId = useSelector(getGroupMemberId);

  const {
    isOpen: isSwitchOrganizationOpen,
    onOpen: onSwitchOrganizationOpen,
    onClose: onSwitchOrganizationClose,
  } = useDisclosure();
  const {
    isOpen: isAccountDrawerOpen,
    onOpen: onAccountDrawerOpen,
    onClose: onAccountDrawerClose,
  } = useDisclosure();

  const wrap =
    (fn) =>
    (...args) =>
      dispatch(fn(...args));

  const menuRef = useRef();
  const buttonRef = useRef();

  const isQuotingToolAvailable = useFeatureFlagValue(
    featureFlagKeys.isQuotingToolAvailable,
  );
  const supplierRoles = useSelector(getGroupMemberSupplierRoles);
  const isSuperuser = user?.isSuperuser;
  const isLoggedIn = useSelector(getIsLoggedIn);

  const dispatchLogout = wrap(logout);
  const dispatchOpenChangeUserGroupModal = wrap(
    superUserChangeUserGroupModal.open,
  );
  const dispatchOpenFeatureFlagDrawer = wrap(featureFlagDrawer.open);
  const dispatchOpenCopyAnalyticsDrawer = wrap(copyAnalyticsDrawer.open);
  const dispatchOpenIntegrationDrawer = wrap(managedIntegrationDrawer.open);

  const { data: userGroupMembers } = useGetActiveUserGroupMembers();
  mouseKeyBindingService.useMouseKeyBind('alt+\\', {
    next: () => {
      if (!isAccountDrawerOpen) {
        onAccountDrawerOpen();
        buttonRef.current.click();
      }
    },
    rebindDeps: [isAccountDrawerOpen, buttonRef.current],
  });

  mouseKeyBindingService.useMouseKeyBind('alt+\\', {
    next: () => {
      if (isAccountDrawerOpen) {
        onAccountDrawerClose();
        buttonRef.current.click();
      }
    },
    rebindDeps: [isAccountDrawerOpen, menuRef.current],
    context: menuRef.current,
  });

  const groupsCount = userGroupMembers && userGroupMembers.length;

  if (!isLoggedIn) return null;

  return (
    <Menu
      offset={[-8, 0]}
      placement="bottom-end"
      isLazy
      onClose={onAccountDrawerClose}
      autoSelect
    >
      <MenuButton
        as={Box}
        color="feature.onInteraction"
        display="block"
        cursor="pointer"
        height="100%"
        minWidth={0}
        transition="all 0.2s ease"
        _hover={{
          bg: 'page.plateDark',
        }}
        _active={{
          bg: 'feature.primary',
          userSelect: 'none',
        }}
        ref={buttonRef}
      >
        <HStack
          as="button"
          height="100%"
          width="100%"
          minWidth={0}
          justifyContent="flex-end"
          paddingRight={1.5}
          paddingLeft={1.5}
          gap={1.5}
          _focus={{
            boxShadow: 'focus',
            border: 'none',
            br: 'sm',
          }}
        >
          <Nameplate
            textAlign="right"
            isMicroAppHeader={isMicroAppHeader}
            display={{ base: 'none', desktop: 'unset' }}
            flex="1 1 auto"
          />
          <Avatar
            backgroundColor={user.avatarColor}
            name={userFullName}
            size={isMicroAppHeader ? 'sm' : 'md'}
            margin="0 !important"
          />
          <VisuallyHidden>User Menu</VisuallyHidden>
        </HStack>
      </MenuButton>
      <MenuList borderTopRadius={0} borderTop="0px" ref={menuRef}>
        <Box display={{ base: 'unset', desktop: 'none' }}>
          <Flex alignItems="center" gap={1} marginLeft={2} marginRight={2}>
            <Avatar
              backgroundColor={user.avatarColor}
              name={userFullName}
              size="sm"
            />
            <Nameplate textAlign="left" isMicroAppHeader={false} />
          </Flex>
          <MenuDivider />
        </Box>
        <MenuContents
          isQuotingToolAvailable={isQuotingToolAvailable}
          isSuperuser={isSuperuser}
          openChangeUserGroupModal={dispatchOpenChangeUserGroupModal}
          openFeatureFlagDrawer={dispatchOpenFeatureFlagDrawer}
          openCopyAnalyticsDrawer={dispatchOpenCopyAnalyticsDrawer}
          openManagedIntegrationDrawer={dispatchOpenIntegrationDrawer}
          supplierRoles={supplierRoles}
          logout={dispatchLogout}
          onOpen={onSwitchOrganizationOpen}
          isUserMultipleGroupMember={groupsCount > 1 ? true : false}
        />
      </MenuList>
      {userGroupMembers && groupMemberId && (
        <SwitchUserGroupDrawer
          onClose={onSwitchOrganizationClose}
          isOpen={isSwitchOrganizationOpen}
          userGroupMembers={userGroupMembers}
          groupMemberId={groupMemberId}
        />
      )}
    </Menu>
  );
};

export default NavDropdown;
